import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Users as UsersIcon, Shield, CheckCircle, Trash2, UserCog, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import BackButton from '../../components/BackButton';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, admins: 0, members: 0, verified: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      
      const admins = response.data.filter(u => u.role === 'admin').length;
      const members = response.data.filter(u => u.role === 'member').length;
      const verified = response.data.filter(u => u.is_verified).length;
      
      setStats({
        total: response.data.length,
        admins,
        members,
        verified
      });
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async (newRole) => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      await api.put(`/users/${selectedUser.user_id}/role?new_role=${newRole}`);
      toast.success(`User role changed to ${newRole}`);
      setIsRoleDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    
    try {
      await api.delete(`/users/${selectedUser.user_id}`);
      toast.success('User deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setIsRoleDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <BackButton to="/admin" />
      
      <div>
        <h1 className="page-header text-white">Users Management</h1>
        <p className="text-slate-400">View and manage user accounts</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card bg-slate-800 border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Total Users</p>
              <p className="text-3xl font-serif font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-blue-900/30 p-3 rounded-lg">
              <UsersIcon className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="card bg-slate-800 border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Administrators</p>
              <p className="text-3xl font-serif font-bold text-white">{stats.admins}</p>
            </div>
            <div className="bg-orange-900/30 p-3 rounded-lg">
              <Shield className="w-6 h-6 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="card bg-slate-800 border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Members</p>
              <p className="text-3xl font-serif font-bold text-white">{stats.members}</p>
            </div>
            <div className="bg-green-900/30 p-3 rounded-lg">
              <UsersIcon className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="card bg-slate-800 border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Verified</p>
              <p className="text-3xl font-serif font-bold text-white">{stats.verified}</p>
            </div>
            <div className="bg-purple-900/30 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card bg-slate-800 border-slate-700 text-center py-12">
          <p className="text-slate-400">Loading users...</p>
        </div>
      ) : users.length > 0 ? (
        <div className="card bg-slate-800 border-slate-700 p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-slate-900/30" data-testid={`user-row-${user.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-slate-700 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-white">{user.full_name?.charAt(0)}</span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{user.full_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-300">{user.email}</div>
                      <div className="text-sm text-slate-400">{user.phone}</div>
                    </td>
                    <td className="px-6 py-4">
                      {user.role === 'admin' ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-900/30 text-orange-400">Admin</span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">Member</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.is_verified ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400">Verified</span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/30 text-red-400">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openRoleDialog(user)}
                          className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-700 rounded transition-colors"
                          title="Change Role"
                          data-testid={`change-role-${user.user_id}`}
                        >
                          <UserCog className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteDialog(user)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                          title="Delete User"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card bg-slate-800 border-slate-700 text-center py-12">
          <p className="text-slate-400">No users found</p>
        </div>
      )}

      {/* Change Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Change User Role</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-700 rounded-lg">
                <p className="text-sm text-slate-400">User</p>
                <p className="font-medium text-white">{selectedUser.full_name}</p>
                <p className="text-sm text-slate-400">{selectedUser.email}</p>
                <p className="text-sm mt-2 text-slate-400">Current Role: 
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs ${selectedUser.role === 'admin' ? 'bg-orange-900/50 text-orange-300' : 'bg-slate-600 text-slate-300'}`}>
                    {selectedUser.role}
                  </span>
                </p>
              </div>
              <div className="flex space-x-3">
                <Button
                  onClick={() => handleChangeRole('admin')}
                  disabled={actionLoading || selectedUser.role === 'admin'}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
                  data-testid="set-admin-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Make Admin
                </Button>
                <Button
                  onClick={() => handleChangeRole('member')}
                  disabled={actionLoading || selectedUser.role === 'member'}
                  className="flex-1 bg-slate-600 hover:bg-slate-500 disabled:opacity-50"
                  data-testid="set-member-btn"
                >
                  <UsersIcon className="w-4 h-4 mr-2" />
                  Make Member
                </Button>
              </div>
              <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)} className="w-full">
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-400" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg">
                <p className="text-red-300 text-sm mb-3">This action cannot be undone. The following user will be permanently deleted:</p>
                <p className="font-medium text-white">{selectedUser.full_name}</p>
                <p className="text-sm text-slate-400">{selectedUser.email}</p>
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {actionLoading ? 'Deleting...' : 'Delete User'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
